import type * as T from "@src/types";
import {
  type TrackingEndedFactBounds,
  isPositionFinanciallyClosed,
} from "@src/utils/positionStatus";
import { apiPeriodToMinutes } from "@src/components/TradingView/datafeed/helpers";
import type { BacktestChartDefinitionItem } from "@tradingdroid/frontends-types";

import type { LineWidth } from "lightweight-charts";

import { hexToRgba } from "./indicators";

export const getCachedCandleBounds = (
  candles?: T.TypedArrayCandles | null,
  apiPeriod?: string,
): TrackingEndedFactBounds | null => {
  const times = candles?.times;
  if (!times?.length) return null;
  const seconds = apiPeriod ? apiPeriodToMinutes(apiPeriod) * 60 : 0;
  return { from: times[times.length - 1], to: times[0] + seconds, upperExclusive: seconds > 0 };
};

/**
 * Dynamic data extraction from parquet records using sourcekey
 * @param isSparse - If true, null values become NaN (creates gaps). If false, null becomes 0.
 */
const extractDataBySourceKey = (
  data: T.ProcessedCacheData,
  sourcekey: string,
  isSparse: boolean = false,
): number[] => {
  // Sparse indicators: null → NaN (creates gaps in chart lines)
  // Dense indicators: null → 0 (continuous line)
  const nullValue = isSparse ? NaN : 0;

  return data.map((record) => {
    // Handle special timestamp fields
    if (sourcekey === "open_time") return record["open time"];
    if (sourcekey === "close_time") return record["close time"];

    // Direct property access for regular fields
    const value = record[sourcekey];

    if (typeof value === "number") {
      return value;
    }

    // Handle null values - sparse indicators get NaN, others get 0
    if (value === null || value === undefined) {
      return nullValue;
    }

    // Try to convert to number
    const numValue = Number(value);
    if (!isNaN(numValue)) {
      return numValue;
    }

    // Default fallback
    return nullValue;
  });
};

/**
 * Validates that a sourcekey exists in the data
 */
const validateSourceKey = (data: T.ProcessedCacheData, sourcekey: string): boolean => {
  if (data.length === 0) return false;

  const firstRecord = data[0];

  // Check special timestamp fields
  if (sourcekey === "open_time") return "open time" in firstRecord;
  if (sourcekey === "close_time") return "close time" in firstRecord;

  // Check regular fields
  return sourcekey in firstRecord;
};

/**
 * Creates a series from BacktestChartDefinitionItem using dynamic sourcekey mapping
 */
const detectIndicatorType = (definition: BacktestChartDefinitionItem): "line" | "volume" => {
  const sourceKey = definition.sourcekey?.toLowerCase();
  const name = definition.name?.toLowerCase();

  if (sourceKey === "volume" || name?.includes("volume")) {
    return "volume";
  }

  return "line";
};

export const createSeriesFromDefinition = (
  indicatorDefinition: BacktestChartDefinitionItem,
  cacheData: T.ProcessedCacheData,
): T.SerialLineIndicator | null => {
  // Validate sourcekey exists - return null if not found
  if (!validateSourceKey(cacheData, indicatorDefinition.sourcekey)) {
    console.warn(
      `Skipping indicator "${indicatorDefinition.name}" - sourcekey "${indicatorDefinition.sourcekey}" does not exist in data`,
    );
    return null;
  }

  // Extract values - sparse indicators use NaN for gaps, dense use 0
  const values = extractDataBySourceKey(
    cacheData,
    indicatorDefinition.sourcekey,
    indicatorDefinition.sparse,
  );

  const timestamps = cacheData.map((r) => r["open time"] / 1000);

  // TODO: clarify this "type" / "plotype" mapping, it seems yeah we need both "repeated"
  const indicatorType = detectIndicatorType(indicatorDefinition);

  // Keep ALL data points including NaN - TradingView handles NaN as "no value" (creates gaps)
  // This is simpler than sparse arrays because binary search always finds exact timestamp match
  const data: Array<{ time: number; value: number }> = timestamps.map((time, index) => ({
    time,
    value: values[index], // NaN passes through - indicator will return NaN for gaps
  }));

  return {
    name: indicatorDefinition.name,
    type: indicatorType,
    position: indicatorDefinition.position === "main" ? "chart" : "pane",
    panel: indicatorDefinition.panel,
    zone: indicatorDefinition.zone,
    style: {
      linestyle: indicatorDefinition.style.linestyle,
      plotype: indicatorDefinition.style.plotype as any,
      linewidth: indicatorDefinition.style.linewidth,
      transparency: indicatorDefinition.style.transparency,
      color: indicatorDefinition.style.color,
    },
    data,
  };
};

/**
 * Find the closest candle timestamp to a given position timestamp
 */
const findClosestCandleTimestamp = (
  positionTimestamp: number,
  candleTimestamps: number[],
): number | null => {
  let closest: number | null = null;
  let minDiff = Infinity;

  for (const candleTs of candleTimestamps) {
    const diff = Math.abs(candleTs - positionTimestamp);
    if (diff < minDiff) {
      minDiff = diff;
      closest = candleTs;
    }
  }

  return closest;
};

/**
 * Get timestamps where positions were closed, matched to actual candle timestamps
 * Only uses exit timestamps to avoid duplicate checkpoints (exit of one position = entry of next)
 */
export const getPositionCheckpointTimestamps = (
  positions: T.TDT.ApiPosition[],
  cacheData: T.ProcessedCacheData,
): Set<number> => {
  const timestamps = new Set<number>();
  const candleTimestamps = cacheData.map((r) => r["open time"] / 1000);

  positions.forEach((position) => {
    // Only add position exit timestamp to avoid duplicates
    // (exit of position N often coincides with entry of position N+1)
    if (position.end_ts && isPositionFinanciallyClosed(position)) {
      const posTimestamp = new Date(position.end_ts).getTime() / 1000;
      const closestCandle = findClosestCandleTimestamp(posTimestamp, candleTimestamps);
      if (closestCandle) {
        timestamps.add(closestCandle);
      }
    }
  });

  return timestamps;
};

/**
 * Converts BacktestChartDefinitionItem to TVLightSeries format
 * Returns null if sourcekey doesn't exist in data
 * Filters data to only include position checkpoint timestamps
 */
const createTVLightSeriesFromDefinition = (
  indicatorDefinition: BacktestChartDefinitionItem,
  cacheData: T.ProcessedCacheData,
  positionTimestamps: Set<number>,
): T.TVLightSeries | null => {
  // Validate sourcekey exists - return null if not found
  if (!validateSourceKey(cacheData, indicatorDefinition.sourcekey)) {
    console.warn(
      `Skipping series "${indicatorDefinition.name}" - sourcekey "${indicatorDefinition.sourcekey}" does not exist in data`,
    );
    return null;
  }

  // Extract values - sparse indicators use NaN for gaps, dense use 0
  const values = extractDataBySourceKey(
    cacheData,
    indicatorDefinition.sourcekey,
    indicatorDefinition.sparse,
  );
  const timestamps = cacheData.map((r) => (r["open time"] / 1000) as T.TVL.Time);

  // Map plotype to TVLight series type
  let seriesType: T.TVL.SeriesType = "Line";
  switch (indicatorDefinition.style.plotype) {
    case "area":
      seriesType = "Area";
      break;
    case "histogram":
      seriesType = "Histogram";
      break;
    case "line":
    default:
      seriesType = "Line";
      break;
  }

  // Convert hex color and transparency to rgba
  const transparency = indicatorDefinition.style.transparency / 100;
  const opacity = 1 - transparency;
  const color = hexToRgba(indicatorDefinition.style.color, opacity);

  // Filter data to only include position checkpoint timestamps
  // NaN values are filtered out since lightweight charts don't handle them well
  const data = timestamps
    .map((time, index) => ({
      time,
      value: values[index],
    }))
    .filter((point) => {
      // Must be a position checkpoint timestamp
      if (!positionTimestamps.has(point.time as number)) return false;
      // Must have a valid value (not NaN, null, undefined) - lightweight charts need real values
      if (point.value == null || Number.isNaN(point.value)) return false;
      return true;
    });

  // Clamp linewidth to valid LineWidth values (1-4)
  const validLineWidth = Math.min(
    Math.max(Math.round(indicatorDefinition.style.linewidth), 1),
    4,
  ) as LineWidth;

  // Drawdown is presentation-only overridden: translucent so it reads as a backdrop, and moved to
  // the inverted left scale so the bars hang down from 0 instead of competing with the capital curve.
  if (indicatorDefinition.sourcekey === "drawdown_pct") {
    return {
      type: seriesType,
      data,
      options: {
        title: indicatorDefinition.name,
        color: "rgba(139, 0, 0, 0.3)",
        priceScaleId: "left",
      },
      priceScaleId: "left",
    };
  }

  return {
    type: seriesType,
    data,
    options: {
      title: indicatorDefinition.name,
      color,
      lineWidth: validLineWidth,
    },
  };
};

const createCandleIndicator = (cacheData: T.ProcessedCacheData): T.SerialCandleIndicator => ({
  name: "Price",
  type: "candle",
  position: "chart",
  style: {
    linestyle: "solid",
    plotype: "candlestick",
    linewidth: 1,
    transparency: 0,
    color: "#2196F3",
  },
  data: cacheData.map((record) => ({
    time: (record["open time"] || record["open_time"]) / 1000,
    open: record.open,
    high: record.high,
    low: record.low,
    close: record.close,
    volume: record.volume,
  })),
});

const createVolumeIndicator = (cacheData: T.ProcessedCacheData): T.SerialLineIndicator => {
  const volumeData = cacheData.map((record) => ({
    time: (record["open time"] || record["open_time"]) / 1000,
    value: record.volume || 0,
  }));

  return {
    name: "Volume",
    type: "volume",
    position: "chart",
    style: {
      linestyle: "solid",
      plotype: "histogram",
      linewidth: 1,
      transparency: 0,
      color: "#7E57C2",
    },
    data: volumeData,
  };
};

/**
 * Converts ProcessedCacheData to RunResult format with API-driven chart configuration
 */
export const processRunResult = (
  run: T.ApiRun,
  symbol: T.TDT.ApiSymbol,
  positions: T.AppPosition[],
  cacheData: T.ProcessedCacheData,
): T.RunResult => {
  if (!cacheData || cacheData.length === 0) {
    throw new Error("No data to convert");
  }

  // Get date range from parquet data
  // Filter parquet data to only include data from run start date onwards
  // This prevents loading years of unnecessary historical data for running executions
  let filteredCacheData = cacheData;
  if (run.start_ts) {
    const runStartTimestamp = new Date(run.start_ts).getTime();
    filteredCacheData = cacheData.filter((record) => {
      const recordTimestamp = record["open time"];
      return recordTimestamp >= runStartTimestamp;
    });
  }

  const candles = filteredCacheData.map((record) => ({
    time: (record["open time"] || record["open_time"]) / 1000, // Convert milliseconds to seconds for TradingView and handle both field names
    open: record.open,
    high: record.high,
    low: record.low,
    close: record.close,
    volume: record.volume,
  }));

  const baseIndicators: T.SerialIndicator[] = [
    createCandleIndicator(filteredCacheData),
    createVolumeIndicator(filteredCacheData),
  ];

  // Enable ALL chart panel indicators for the advanced widget
  // A definition cached without panels draws only its price chart items, as before panels were recorded: its pane
  // items carry no panel to share a pane by
  const drawsPaneItems = run.cache_chart_definition?.panels != null;
  const definitionIndicators: T.SerialIndicator[] = run.cache_chart_definition
    ? run.cache_chart_definition.items
        .filter((i) => i.widget === "advanced")
        .filter((i) => drawsPaneItems || i.position === "main")
        .map((i: BacktestChartDefinitionItem) => createSeriesFromDefinition(i, filteredCacheData))
        .filter((s): s is T.SerialLineIndicator => s !== null) // Remove null entries
    : [];

  const indicators: T.SerialIndicator[] = [...baseIndicators, ...definitionIndicators];

  // Get position checkpoint timestamps for filtering series data
  const positionTimestamps = getPositionCheckpointTimestamps(
    run.positions || [],
    filteredCacheData,
  );

  // Create series for TradingView Lightweight Charts from chart definition.
  // Buy & hold and drawdown come from the cached backend columns, never recomputed here.
  const series: T.TVLightSeries[] = run.cache_chart_definition
    ? run.cache_chart_definition.items
        .filter((i) => i.widget === "lightweight")
        .map((i: BacktestChartDefinitionItem) =>
          createTVLightSeriesFromDefinition(i, filteredCacheData, positionTimestamps),
        )
        .filter((s): s is T.TVLightSeries => s !== null) // Remove null entries
    : [];

  // The height ratios the strategy panels declare, by panel name; a definition cached before panels
  // were recorded has none
  const panelHeightRatios: Record<string, number> = {};
  run.cache_chart_definition?.panels?.forEach((panel) => {
    if (typeof panel.height_ratio === "number") panelHeightRatios[panel.name] = panel.height_ratio;
  });

  return {
    api_period: run.config?.timeframe ?? "5m",
    candles,
    indicators,
    positions: positions || [],
    symbol,
    initial_capital: run.config?.portfolio?.initial_capital || 1000,
    metrics: run.metrics,
    series,
    start_ts: run.start_ts,
    end_ts: run.end_ts,
    panelHeightRatios,
  };
};
